import type {RouteRecordRaw} from 'vue-router';
import {SubscriptionFeature} from '@/constants/subscriptionFeatures';

export const accountSettingsRoutes: RouteRecordRaw[] = [
  {
    name: 'account-settings',
    path: '/settings/account',
    meta: {
      title: (t) => t('settings.account.title', 2),
    },
    component: () => import('@/views/Settings/SettingsIndex.vue'),
    children: [
      {
        path: 'contact',
        name: 'account-settings-contact',
        redirect: {
          name: 'account-settings-my-contact-info',
        },
        meta: {
          permissions: ['selectApiKey', 'selectWebhookSubscription', 'updateShopGeneralSettings'],
          title: (t) => t('settings.general'),
        },
      },
      {
        path: 'packing-tables',
        children: [
          {
            path: '',
            name: 'account-settings-packing-tables',
            // We do not use the RouterView of the parent, because the template of the PackagingTable differs from the parent.
            component: () => import('@/views/PackingTables/PackingTablesOverview/PackingTablesOverview.vue'),
            meta: {
              redirectTo: {name: 'account-settings-packing-tables-restricted'},
              subscriptionFeatures: [SubscriptionFeature.AllowDirectPrinting],
              title: (t) => t('printing.printerGroup.title', 2),
            },
          },
          {
            path: 'create',
            name: 'account-settings-packing-tables-create',
            // We do not use the RouterView of the parent, because the template of the PackagingTable differs from the parent.
            component: () => import('@/views/PackingTables/PackingTablesCreate/PackingTablesCreate.vue'),
            meta: {
              redirectTo: {name: 'account-settings-packing-tables-restricted'},
              subscriptionFeatures: [SubscriptionFeature.AllowDirectPrinting],
              title: (t) => t('printing.printerGroup.title', 2),
            },
          },
          {
            path: ':printerGroupId',
            name: 'account-settings-packing-tables-edit',
            props: true,
            // We do not use the RouterView of the parent, because the template of the PackagingTable differs from the parent.
            component: () => import('@/views/PackingTables/PackingTablesEdit/PackingTablesEdit.vue'),
            meta: {
              redirectTo: {name: 'account-settings-packing-tables-restricted'},
              subscriptionFeatures: [SubscriptionFeature.AllowDirectPrinting],
              title: (t) => t('printing.printerGroup.title', 2),
            },
          },
          {
            path: 'no-preview',
            name: 'account-settings-packing-tables-restricted',
            components: {
              settings: () =>
                import('@/views/PackingTables/PackingTablesRestrictedAccess/PackingTablesRestrictedAccess.vue'),
            },
            meta: {
              activeRouteNameAliases: ['account-settings-packing-tables'],
              title: (t) => t('printing.printerGroup.title', 2),
            },
          },
        ],
      },
      {
        path: 'orders-shipping',
        name: 'account-settings-orders-shipping',
        alias: 'my-contracts',
        components: {
          settings: () => import('@/views/Settings/Account/OrdersShipping/OrdersShipping.vue'),
        },
        meta: {
          permissions: ['updateAccount'],
          title: (t) => t('settings.account.tabs.ordersShipping'),
        },
      },
      {
        path: 'my-contracts',
        name: 'account-settings-my-contracts',
        redirect: {
          name: 'account-settings-orders-shipping',
        },
      },
      {
        path: 'subscriptions',
        name: 'account-settings-subscriptions',
        redirect: {
          name: 'account-settings-orders-shipping',
        },
      },
      {
        path: 'webshops',
        name: 'account-settings-webshops',
        redirect: {
          name: 'account-settings-shops',
        },
      },
      {
        path: 'shops',
        name: 'account-settings-shops',
        components: {
          settings: () => import('@/views/Settings/Account/Shops/Shops.vue'),
        },
        meta: {
          title: (t) => t('shops'),
        },
      },
      {
        path: 'users-security',
        meta: {
          permissions: ['selectUser', 'insertUser', 'updateUser', 'deleteUser'],
        },
        children: [
          {
            path: '',
            name: 'account-settings-users-security',
            components: {
              settings: () => import('@/views/Settings/Account/UsersSecurity/UsersSecurity.vue'),
            },
            meta: {
              title: (t) => t('settings.account.tabs.usersSecurity'),
            },
          },
          {
            path: 'create',
            name: 'account-settings-users-security-create-user',
            component: () => import('@/views/Settings/Account/UsersSecurity/CreateUser/CreateUser.vue'),
            meta: {
              permissions: ['selectApiKey', 'selectWebhookSubscription', 'updateShopGeneralSettings'],
              title: (t) => t('settings.account.users.create'),
            },
          },
          {
            path: ':id',
            props: true,
            name: 'account-settings-users-security-edit-user',
            component: () => import('@/views/Settings/Account/UsersSecurity/EditUser/EditUser.vue'),
            meta: {
              permissions: ['selectApiKey', 'selectWebhookSubscription', 'updateShopGeneralSettings'],
              title: (t) => t('settings.account.users.edit'),
            },
          },
        ],
      },
      {
        path: 'my-contact-info',
        name: 'account-settings-my-contact-info',
        components: {
          settings: () => import('@/views/Settings/Account/MyContactInfo/MyContactInfo.vue'),
        },
        meta: {
          title: (t) => t('settings.account.tabs.myContactInfo'),
        },
      },
      {
        path: 'my-login-info',
        name: 'account-settings-my-login-info',
        components: {
          settings: () => import('@/views/Settings/Account/MyLoginInfo/MyLoginInfo.vue'),
        },
        meta: {
          title: (t) => t('settings.account.tabs.myLoginInfo'),
        },
      },
    ],
  },
];
